import { useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { Button, Dialog, EmptyState, Skeleton, StatusBadge } from '../components/ui.tsx';
import { t } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { dateTime, day, money } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from './manage/common.tsx';

/**
 * Closing financial years: each year's profit or loss goes to retained earnings on its last day,
 * and the year is closed for good. The next year starts from the balances it left.
 */

interface FiscalYear {
  start: string;
  end: string;
  state: 'open' | 'ready' | 'waiting' | 'closed';
  income: number;
  expenses: number;
  profit: number;
  drafts: number;
  closed: { at: string; entry: string | null; number: number | null; profit: number } | null;
}
interface YearsData {
  years: FiscalYear[];
  year_start: string;
  today: string;
  retained: { id: string; code: string; name: string } | null;
}

/** "2026", or "2026/27" for a year that does not start in January. */
const yearName = (y: FiscalYear) => (y.start.slice(0, 4) === y.end.slice(0, 4) ? y.start.slice(0, 4) : `${y.start.slice(0, 4)}/${y.end.slice(2, 4)}`);

export function YearEndTab({ version, onOpenEntry, onBalances, onClosed }: { version: number; onOpenEntry: (id: string) => void; onBalances: (y: { start: string; end: string }) => void; onClosed: () => void }) {
  const { can } = useSession();
  const [v, setV] = useState(0);
  const { data } = useAction<YearsData>('accounting.years', { v: version + v });
  const [closing, setClosing] = useState<FiscalYear | null>(null);
  if (!data) return <Skeleton h={300} r="var(--radius-lg)" />;
  const retained = data.retained ? `${data.retained.code} ${data.retained.name}` : '';
  return (
    <div className="ye">
      <p className="callout callout-info ye-intro" role="note">
        <Icon name="info" size={16} />
        <span>{t('ye.intro', { account: retained })}</span>
      </p>
      {!data.years.length ? (
        <EmptyState icon="calendar" title={t('ye.none')} body={t('ye.none_body')} />
      ) : (
        <ol className="ye-list">
          {data.years.map((y) => (
            <li key={y.end} className={`panel ye-year ye-${y.state}`}>
              <div className="ye-head">
                <div>
                  <h2>{t('ye.year', { name: yearName(y) })}</h2>
                  <p className="muted small">{t('rep.range', { from: day(y.start), to: day(y.end) })}</p>
                </div>
                {y.state === 'closed' ? (
                  <StatusBadge tone="ok" icon="lock" label={t('ye.s_closed')} />
                ) : y.state === 'ready' ? (
                  <StatusBadge tone="warn" icon="clock" label={t('ye.s_ready')} />
                ) : y.state === 'waiting' ? (
                  <StatusBadge tone="neutral" icon="clock" label={t('ye.s_waiting')} />
                ) : (
                  <StatusBadge tone="info" icon="calendar" label={t('ye.s_open')} />
                )}
              </div>
              <dl className="ye-figures">
                <div>
                  <dt>{t('ye.income')}</dt>
                  <dd className="tabular">{money(y.income)}</dd>
                </div>
                <div>
                  <dt>{t('ye.expenses')}</dt>
                  <dd className="tabular">{money(y.expenses)}</dd>
                </div>
                <div>
                  <dt>{y.profit >= 0 ? t('ye.profit') : t('ye.loss')}</dt>
                  <dd className={`tabular ye-result ${y.profit >= 0 ? 'up' : 'down'}`}>{money(Math.abs(y.profit))}</dd>
                </div>
              </dl>
              <div className="ye-foot">
                <span className="muted small">
                  {y.state === 'closed' && y.closed
                    ? y.closed.number
                      ? t('ye.closed_on', { when: dateTime(y.closed.at), number: y.closed.number })
                      : t('ye.closed_empty', { when: dateTime(y.closed.at) })
                    : y.state === 'ready'
                      ? y.drafts
                        ? t('ye.drafts', { n: y.drafts })
                        : t('ye.ready_hint')
                      : y.state === 'waiting'
                        ? t('ye.waiting_hint')
                        : t('ye.open_hint', { end: day(y.end) })}
                </span>
                <div className="ye-actions">
                  {y.state === 'closed' && y.closed?.entry ? (
                    <Button size="sm" variant="ghost" icon="note" onClick={() => onOpenEntry(y.closed!.entry!)}>
                      {t('ye.see_entry')}
                    </Button>
                  ) : null}
                  {y.state !== 'open' ? (
                    <Button size="sm" variant="ghost" icon="receipt" onClick={() => onBalances(y)}>
                      {t('ye.balances')}
                    </Button>
                  ) : null}
                  {y.state === 'ready' && can('accounting.settings') ? (
                    <Button size="sm" variant="primary" icon="lock" disabled={y.drafts > 0} onClick={() => setClosing(y)}>
                      {t('ye.close', { name: yearName(y) })}
                    </Button>
                  ) : null}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
      {closing ? (
        <CloseYear
          year={closing}
          retained={retained}
          onClose={() => setClosing(null)}
          onDone={(entry) => {
            setClosing(null);
            setV((x) => x + 1);
            onClosed();
            if (entry) onOpenEntry(entry);
          }}
        />
      ) : null}
    </div>
  );
}

function CloseYear({ year, retained, onClose, onDone }: { year: FiscalYear; retained: string; onClose: () => void; onDone: (entry: string | null) => void }) {
  const { busy, run } = useSave();
  const { me } = useSession();
  // Closing a year is final: only after signing in with the authenticator app.
  const secondStep = !!me?.mfa;
  const [sure, setSure] = useState(false);
  const close = async () => {
    let entry: string | null = null;
    const ok = await run(async () => {
      const r = (await action('accounting.close_year', { end: year.end })) as { entry: { id: string } | null };
      entry = r.entry?.id ?? null;
    }, t('ye.closed_ok', { name: yearName(year) }));
    if (ok) onDone(entry);
  };
  return (
    <Dialog
      title={t('ye.close', { name: yearName(year) })}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="lock" busy={busy} disabled={!sure || !secondStep} onClick={() => void close()}>
            {t('ye.close', { name: yearName(year) })}
          </Button>
        </>
      }
    >
      <dl className="ye-summary">
        <div>
          <dt>{t('ye.period')}</dt>
          <dd>{t('rep.range', { from: day(year.start), to: day(year.end) })}</dd>
        </div>
        <div>
          <dt>{year.profit >= 0 ? t('ye.profit') : t('ye.loss')}</dt>
          <dd className={`tabular ye-result ${year.profit >= 0 ? 'up' : 'down'}`}>{money(Math.abs(year.profit))}</dd>
        </div>
        <div>
          <dt>{t('ye.goes_to')}</dt>
          <dd>{retained}</dd>
        </div>
      </dl>
      <p className="muted small">{t('ye.what_happens', { end: day(year.end) })}</p>
      {!secondStep ? (
        <p className="callout callout-warn small" role="note">
          <Icon name="shield" size={16} /> {t('ye.needs_mfa')}
        </p>
      ) : null}
      <label className="check-row">
        <input type="checkbox" checked={sure} onChange={(e) => setSure(e.target.checked)} data-autofocus />
        <span>{t('ye.sure')}</span>
      </label>
    </Dialog>
  );
}
